import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface CountdownProps {
  targetDate: Date;
  className?: string;
  textColor?: string;
  numberColor?: string;
  promoCode?: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const Countdown = ({ targetDate, textColor = "text-white/90", numberColor = "text-red-400", className = "", promoCode }: CountdownProps) => {
  const { t, i18n } = useTranslation('common');
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const codeForLabel = promoCode;

  const getLabelKey = () => {
    const code = codeForLabel || 'WEBSITE';
    const promoKey = `promo.${code}.label`;
    if (!i18n.exists(promoKey)) return 'countdown.default.label';

    return promoKey;
  };

  useEffect(() => {
    const calculateTimeLeft = (): TimeLeft => {
      const now = new Date().getTime();
      const target = targetDate.getTime();
      const difference = target - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        return { days, hours, minutes, seconds };
      }

      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    // Calculate initial time
    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, [targetDate]);

  const formatNumber = (num: number): string => {
    return num.toString() //.padStart(2, '0');
  };

  const labelKey = getLabelKey();

  return (
    <div className={`flex items-baseline gap-1 ${textColor} ${className}`}>
      <span className="text-sm md:text-base font-medium">{t(labelKey, { defaultValue: t('countdown.default.label') })}</span>
      <div className="flex items-baseline gap-1.5 font-mono">

        <span className={`${numberColor} pl-1 rounded text-sm md:text-base`}>
          {timeLeft.days}
        </span>
        <span className="text-xs md:text-sm">{t('countdown.days')}</span>

        <span className={`${numberColor} pl-1 rounded text-sm md:text-base`}>
          {formatNumber(timeLeft.hours)}
        </span>
        <span className="text-xs md:text-sm">{t('countdown.hours')}</span>

        <span className={`${numberColor} pl-1 rounded text-sm md:text-base`}>
          {formatNumber(timeLeft.minutes)}
        </span>
        <span className="text-xs md:text-sm">{t('countdown.minutes')}</span>

        <span className={`${numberColor} pl-1 rounded text-sm md:text-base`}>
          {formatNumber(timeLeft.seconds)}
        </span>
        <span className="text-xs md:text-sm">{t('countdown.seconds')}</span>
      </div>
    </div>
  );
};

export default Countdown;
